import React, { Suspense, ComponentType } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

interface LazySectionProps {
  children: React.ReactNode
  threshold?: number
  rootMargin?: string
  className?: string
}

export const LazySection: React.FC<LazySectionProps> = ({
  children,
  threshold = 0.1,
  rootMargin = '100px 0px',
  className = ''
}) => {
  const [ref, inView] = useInView({
    threshold,
    rootMargin,
    triggerOnce: true
  })

  return (
    <div ref={ref} className={className}>
      {inView ? children : <SectionSkeleton />}
    </div>
  )
}

const SectionSkeleton: React.FC = () => {
  return (
    <div className="py-24 animate-pulse">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="h-8 bg-neutral-200 rounded-full w-3/4 mx-auto mb-4"></div>
          <div className="h-4 bg-neutral-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-neutral-200 rounded w-2/3 mx-auto"></div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-64 bg-neutral-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    </div>
  )
}

// HOC for lazy loading sections
export function withLazyLoading<P extends object>(
  Component: ComponentType<P>,
  loadingComponent?: ComponentType
): ComponentType<P> {
  const WrappedComponent = (props: P) => {
    const LoadingComponent = loadingComponent || SectionSkeleton
    
    return (
      <Suspense fallback={<LoadingComponent />}>
        <LazySection>
          <Component {...props} />
        </LazySection>
      </Suspense>
    )
  }
  
  WrappedComponent.displayName = `withLazyLoading(${Component.displayName || Component.name})`
  
  return WrappedComponent
}

// Preload components
export const preloadComponent = (componentImport: () => Promise<any>) => {
  return componentImport()
}

// Image lazy loading component
interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string
  alt: string
  placeholder?: string
  blurDataURL?: string
}

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  placeholder,
  blurDataURL,
  className = '',
  ...props
}) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    rootMargin: '50px 0px',
    triggerOnce: true
  })

  const [loaded, setLoaded] = React.useState(false)
  const [error, setError] = React.useState(false)

  return (
    <div ref={ref} className={`relative ${className}`}>
      {inView && (
        <>
          {/* Blur placeholder */}
          {blurDataURL && !loaded && (
            <motion.img
              src={blurDataURL}
              alt=""
              className="absolute inset-0 w-full h-full object-cover filter blur-sm"
              initial={{ opacity: 1 }}
              animate={{ opacity: loaded ? 0 : 1 }}
            />
          )}
          
          {/* Main image */}
          <motion.img
            src={src}
            alt={alt}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            initial={{ opacity: 0 }}
            animate={{ opacity: loaded ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full object-cover"
            {...(props as any)}
          />
          
          {/* Error state */}
          {error && (
            <div className="absolute inset-0 bg-neutral-200 flex items-center justify-center">
              <span className="text-neutral-500 text-sm">Failed to load image</span>
            </div>
          )}
        </>
      )}
      
      {/* Loading placeholder */}
      {!inView && (
        <div className={`bg-neutral-200 animate-pulse ${className}`}>
          {placeholder && (
            <div className="flex items-center justify-center h-full">
              <span className="text-neutral-400">{placeholder}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

